import React, { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  formatPriceRange,
  demoSid,
  reportFunnelEvent as reportDemoEvent,
} from "@/lib/utils";

/**
 * Public demo: no account, no card.
 *
 * Deliberately built in the app's own visual language — mobile column, product
 * tiles with emoji, meal cards with a colour-coded left edge, segmented pills —
 * so the demo is a slice of the real thing rather than a page describing it.
 * Someone who likes what they touch here should recognise it after signing up.
 *
 * The fridge lives in localStorage and never reaches our servers.
 */

// Mirrors DEMO_STAPLES in backend/server.py. Index order matters: removals are
// tracked by index so switching language keeps the same items removed.
const STAPLES = {
  uk: ["сіль", "перець", "олія", "вода"],
  en: ["salt", "pepper", "cooking oil", "water"],
  ru: ["соль", "перец", "масло растительное", "вода"],
};

// The reporter moved to lib/utils so the landing page and the demo record steps
// through the same code path. Two copies of it would drift, and a funnel whose
// steps are measured slightly differently is worse than no funnel.

const MIN_ITEMS = 5;
const MAX_ITEMS = 10;
const LS_ITEMS = "mp_demo_items";
const LS_REMOVED = "mp_demo_removed_staples";

// The app shows a product emoji per item; the demo has no catalogue lookup, so
// it guesses from the name. A wrong guess costs nothing — a missing one makes
// the grid look unfinished.
const EMOJI = [
  [["курк", "куряч", "курица", "chicken"], "🍗"],
  [["яйц", "яйц", "яйца", "egg"], "🥚"],
  [["рис", "rice"], "🍚"],
  [["гречк", "buckwheat"], "🌾"],
  [["макарон", "паст", "pasta", "spaghetti"], "🍝"],
  [["хліб", "хлеб", "bread"], "🍞"],
  [["молок", "молоко", "milk"], "🥛"],
  [["сир", "творог", "cheese", "curd"], "🧀"],
  [["йогурт", "yog"], "🥣"],
  [["масл", "олі", "oil", "butter"], "🫒"],
  [["помідор", "томат", "помидор", "tomato"], "🍅"],
  [["огірок", "огурец", "cucumber"], "🥒"],
  [["картопл", "картофел", "potato"], "🥔"],
  [["моркв", "морков", "carrot"], "🥕"],
  [["цибул", "лук", "onion"], "🧅"],
  [["часник", "чеснок", "garlic"], "🧄"],
  [["броколі", "брокколи", "broccoli"], "🥦"],
  [["капуст", "cabbage"], "🥬"],
  [["перец", "перець", "pepper"], "🌶️"],
  [["гриб", "mushroom"], "🍄"],
  [["кабач", "zucchini"], "🥒"],
  [["яблук", "яблок", "apple"], "🍎"],
  [["банан", "banana"], "🍌"],
  [["лимон", "lemon"], "🍋"],
  [["риб", "рыб", "лосос", "fish", "salmon", "tuna"], "🐟"],
  [["яловичин", "говядин", "beef", "steak"], "🥩"],
  [["свинин", "pork"], "🥓"],
  [["квасол", "фасол", "bean", "lentil", "сочевиц", "чечевиц"], "🫘"],
  [["сіль", "соль", "salt"], "🧂"],
  [["вод", "вода", "water"], "💧"],
  [["цукор", "сахар", "sugar"], "🍬"],
  [["борошн", "мук", "flour"], "🌾"],
];

function emojiFor(name) {
  const n = (name || "").toLowerCase();
  for (const [keys, e] of EMOJI) if (keys.some((k) => n.includes(k))) return e;
  return "🥘";
}

// Items were plain strings until the catalogue arrived. Storage written by an
// older build must not break the grid, so everything is coerced on read.
const normaliseItem = (raw) =>
  typeof raw === "string"
    ? { name: raw, icon: emojiFor(raw) }
    : { name: String(raw?.name || ""), icon: raw?.icon || emojiFor(raw?.name || "") };

const ACCENT = {
  breakfast: "#E9C46A",
  lunch: "#7B9E7F",
  dinner: "#D2735A",
  snack: "#A8D5BA",
};

const T = {
  eyebrow: { uk: "Демо · без реєстрації", en: "Demo · no signup", ru: "Демо · без регистрации" },
  title: { uk: "Спробуй просто зараз", en: "Try it right now", ru: "Попробуй прямо сейчас" },
  sub: {
    uk: "Без акаунта і без картки. Наповни холодильник — і подивись, що вийде на день.",
    en: "No account, no card. Fill the fridge and see what a day looks like.",
    ru: "Без аккаунта и без карты. Наполни холодильник — и посмотри, что выйдет на день.",
  },
  tabFridge: { uk: "Холодильник", en: "Fridge", ru: "Холодильник" },
  tabPlan: { uk: "План на день", en: "Your day", ru: "План на день" },

  yourItems: { uk: "Твої продукти", en: "Your items", ru: "Твои продукты" },
  basics: { uk: "Базове", en: "Basics", ru: "Базовое" },
  basicsNote: {
    uk: "Це є майже в кожній кухні — ми додали самі. Прибери те, чого немає.",
    en: "Almost every kitchen has these — we added them. Remove what you do not have.",
    ru: "Это есть почти на каждой кухне — мы добавили сами. Убери то, чего нет.",
  },
  addPlaceholder: { uk: "Що є вдома?", en: "What do you have?", ru: "Что есть дома?" },
  // Shown when the catalogue does not know a typed word. The word is queued for
  // review, so the promise is real rather than a polite brush-off.
  notFound: {
    uk: (n) => `«${n}» у нас поки немає. Дякуємо — записали, найближчим часом додамо.`,
    en: (n) => `We do not have "${n}" yet. Thank you — noted, we will add it soon.`,
    ru: (n) => `«${n}» у нас пока нет. Спасибо — записали, скоро добавим.`,
  },
  emptyHint: {
    uk: "Порожньо. Додай 5–10 продуктів, які маєш удома.",
    en: "Empty. Add 5–10 things you have at home.",
    ru: "Пусто. Добавь 5–10 продуктов, которые есть дома.",
  },
  needMore: {
    uk: (n) => `Ще ${n} — і можна генерувати`,
    en: (n) => `${n} more and you can generate`,
    ru: (n) => `Ещё ${n} — и можно генерировать`,
  },
  generate: { uk: "Згенерувати день", en: "Generate my day", ru: "Сгенерировать день" },
  generating: { uk: "Готуємо день…", en: "Building your day…", ru: "Готовим день…" },
  generatingNote: {
    uk: "Справжня генерація, не заготовка. До півхвилини.",
    en: "The real generator, not a canned result. Up to half a minute.",
    ru: "Настоящая генерация, не заготовка. До полуминуты.",
  },
  fromFridge: { uk: "З наявних продуктів", en: "From available ingredients", ru: "Из имеющихся продуктов" },

  back: { uk: "Назад", en: "Back", ru: "Назад" },
  ingredients: { uk: "Інгредієнти", en: "Ingredients", ru: "Ингредиенты" },
  method: { uk: "Приготування", en: "Method", ru: "Приготовление" },
  kcal: { uk: "ккал", en: "kcal", ru: "ккал" },
  min: { uk: "хв", en: "min", ru: "мин" },
  nutrition: { uk: "Поживність", en: "Nutrition", ru: "Пищевая ценность" },
  protein: { uk: "білки", en: "protein", ru: "белки" },
  carbs: { uk: "вуглеводи", en: "carbs", ru: "углеводы" },
  fat: { uk: "жири", en: "fat", ru: "жиры" },
  gram: { uk: "г", en: "g", ru: "г" },
  // Same honesty the app shows: which share of the ingredients the catalogue
  // could actually resolve into numbers.
  nutritionPartial: {
    uk: (r, t) => `розраховано за ${r} з ${t} інгредієнтів`,
    en: (r, t) => `calculated from ${r} of ${t} ingredients`,
    ru: (r, t) => `рассчитано по ${r} из ${t} ингредиентов`,
  },
  dayTotal: { uk: "Разом за день", en: "Day total", ru: "Всего за день" },

  errFew: { uk: "Треба щонайменше 5 продуктів", en: "At least 5 items, please", ru: "Нужно минимум 5 продуктов" },
  errGeneric: { uk: "Щось пішло не так. Спробуй за хвилину.", en: "Something went wrong. Try again in a minute.", ru: "Что-то пошло не так. Попробуй через минуту." },

  capTitle: { uk: "На сьогодні все", en: "That is it for today", ru: "На сегодня всё" },
  capBody: {
    uk: "Одне демо на день. Кожна генерація справжня, і вона нам чогось коштує. У застосунку їх стільки, скільки треба — і на тиждень, а не на день.",
    en: "One demo a day. Every generation is real and each costs us something. In the app there are as many as you need — for a week, not a day.",
    ru: "Одно демо в день. Каждая генерация настоящая, и она нам чего-то стоит. В приложении их столько, сколько нужно — и на неделю, а не на день.",
  },
  cta: { uk: "Спробувати 7 днів безкоштовно", en: "Start the 7-day trial", ru: "Попробовать 7 дней бесплатно" },
  outroTitle: { uk: "Це був один день", en: "That was one day", ru: "Это был один день" },
  outroBody: {
    uk: "У застосунку MealPill планує тиждень, сам стежить за термінами й збирає список того, чого бракує.",
    en: "In the app MealPill plans a week, watches expiry dates and builds the list of what you are missing.",
    ru: "В приложении MealPill планирует неделю, сам следит за сроками и собирает список того, чего не хватает.",
  },
};

export default function DemoPage() {
  const { lang } = useLanguage();
  const L = (o) => o[lang] || o.en;
  const staples = STAPLES[lang] || STAPLES.en;

  const [tab, setTab] = useState("fridge");
  const [items, setItems] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [draft, setDraft] = useState("");
  const [plan, setPlan] = useState(null);
  const [openMeal, setOpenMeal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [capped, setCapped] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [notFound, setNotFound] = useState(null);
  const [checking, setChecking] = useState(false);
  // Without this the save effect fires on mount with the still-empty initial
  // state and wipes what restore has just read back.
  const [hydrated, setHydrated] = useState(false);

  // Counted by distinct sid on the server, so a refresh is not a new visitor.
  useEffect(() => {
    reportDemoEvent("demo_view");
  }, []);

  useEffect(() => {
    try {
      const a = JSON.parse(localStorage.getItem(LS_ITEMS) || "[]");
      const b = JSON.parse(localStorage.getItem(LS_REMOVED) || "[]");
      if (Array.isArray(a))
        setItems(a.slice(0, MAX_ITEMS).map(normaliseItem).filter((i) => i.name));
      if (Array.isArray(b)) setRemoved(b);
    } catch {
      /* unreadable storage is not worth failing over */
    } finally {
      setHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(LS_ITEMS, JSON.stringify(items));
      localStorage.setItem(LS_REMOVED, JSON.stringify(removed));
    } catch {
      /* private mode refuses writes; the demo still works in memory */
    }
  }, [items, removed, hydrated]);

  // Catalogue autocomplete. The demo used to accept any string: no dropdown
  // appeared, which reads as broken lookup, and the generator received words
  // the catalogue can neither price nor count nutrition for. Same endpoint
  // shape and same cross-language scan the signed-in fridge uses.
  useEffect(() => {
    const q = draft.trim();
    if (!q) return setSuggestions([]);
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/demo/products/search` +
            `?query=${encodeURIComponent(q)}&lang=${lang}&limit=8`
        );
        if (!alive || !res.ok) return;
        const data = await res.json();
        if (alive) setSuggestions(Array.isArray(data?.results) ? data.results : []);
      } catch {
        /* suggestions are a convenience - typing must never break */
      }
    }, 180);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [draft, lang]);

  const pushItem = useCallback(
    (name, icon) => {
      const clean = (name || "").trim().slice(0, 60);
      if (!clean || items.length >= MAX_ITEMS) return;
      if (!items.some((i) => i.name.toLowerCase() === clean.toLowerCase())) {
        setItems((p) => [...p, { name: clean, icon: icon || emojiFor(clean) }]);
      }
      setDraft("");
      setSuggestions([]);
      setNotFound(null);
    },
    [items]
  );

  // Typed text rather than a pick. Anything the catalogue cannot resolve is
  // refused - an ingredient we cannot price or count makes the demo argue
  // against the product - but the word is recorded so the gap becomes visible.
  const addDraft = useCallback(async () => {
    const q = draft.trim();
    if (!q || items.length >= MAX_ITEMS) return;
    const exact = suggestions.find((s) => s.name.toLowerCase() === q.toLowerCase());
    if (exact) return pushItem(exact.name, exact.icon);
    setChecking(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/demo/products/unknown`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: q, language: lang }),
        }
      );
      const data = res.ok ? await res.json() : null;
      // The server runs the full lookup engine, which knows synonyms, subtypes
      // and fuzzy matches the eight suggestions on screen may not show. If it
      // recognises the word, refusing it here would have been wrong.
      if (data?.status === "known") return pushItem(q);
    } catch {
      /* the word is lost, the visitor is not: still thank them */
    } finally {
      setChecking(false);
    }
    setNotFound(q);
    setDraft("");
  }, [draft, items, suggestions, lang, pushItem]);

  const generate = async () => {
    if (items.length < MIN_ITEMS) return setError(L(T.errFew));
    setLoading(true);
    setError(null);
    setCapped(false);
    setOpenMeal(null);
    setTab("plan");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sid: demoSid(),
          ingredients: items.map((i) => i.name),
          staples: staples.filter((_, i) => !removed.includes(i)),
          language: lang,
        }),
      });
      if (res.status === 429) return setCapped(true);
      if (!res.ok) return setError(L(T.errGeneric));
      const data = await res.json();
      if (!data?.days?.length) return setError(L(T.errGeneric));
      setPlan(data);
    } catch {
      setError(L(T.errGeneric));
    } finally {
      setLoading(false);
    }
  };

  const meals = plan?.days?.[0]?.meals || [];
  // Ranges, not point estimates. The backend returns p10/p90 bounds per dish
  // and the app shows them; a single number claims a precision grocery prices
  // do not have. Summing the two bounds is what the planner does too.
  const dayCost = meals.reduce(
    (a, m) => ({
      min: a.min + (m?.cost?.cost_min_total || 0),
      max: a.max + (m?.cost?.cost_max_total || 0),
    }),
    { min: 0, max: 0 }
  );
  const money = (n) => `CA$${Number(n).toFixed(2)}`;
  const needed = MIN_ITEMS - items.length;
  const ready = items.length >= MIN_ITEMS;

  const Tile = ({ label, sub, emoji, muted, onClick, action }) => (
    <button
      onClick={onClick}
      className={`relative flex flex-col items-center justify-center rounded-2xl border px-2 py-4 transition active:scale-95 ${
        muted
          ? "border-dashed border-black/15 bg-transparent opacity-45"
          : "border-black/5 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
      }`}
    >
      <span className="mb-1.5 text-[28px] leading-none">{emoji}</span>
      <span className="max-w-full truncate text-[13px] font-medium text-[#3D4035]">{label}</span>
      {sub && <span className="mt-0.5 text-[11px] text-[#3D4035]/45">{sub}</span>}
      {action && (
        <span className="absolute right-1.5 top-1.5 text-[12px] text-[#3D4035]/25">{action}</span>
      )}
    </button>
  );

  // Nutrition comes from the catalogue, not the model - the same deterministic
  // source the app's recipe page uses. Hidden entirely when nothing resolved,
  // so the demo never shows a confident zero.
  const NutritionStrip = ({ meal }) => {
    const g = L(T.gram);
    const cells = [
      ["#D2735A", Math.round(meal.calories || 0), L(T.kcal)],
      ["#7B9E7F", `${Math.round(meal.protein || 0)}${g}`, L(T.protein)],
      ["#E9C46A", `${Math.round(meal.carbs || 0)}${g}`, L(T.carbs)],
      ["#8C7B6B", `${Math.round(meal.fat || 0)}${g}`, L(T.fat)],
    ];
    if (!cells.some(([, v]) => parseFloat(v) > 0)) return null;
    const partial =
      meal.nutrition_total > 0 &&
      meal.nutrition_resolved != null &&
      meal.nutrition_resolved < meal.nutrition_total;
    return (
      <div className="mb-6">
        <p className="mb-2 text-[12px] uppercase tracking-[0.1em] text-[#3D4035]/40">
          {L(T.nutrition)}
        </p>
        <div className="grid grid-cols-4 gap-2">
          {cells.map(([color, value, label]) => (
            <div
              key={label}
              style={{ backgroundColor: `${color}1A` }}
              className="flex flex-col items-center justify-center rounded-2xl px-2 py-3"
            >
              <span className="text-[20px] font-medium leading-tight">{value}</span>
              <span className="mt-0.5 text-[11px] uppercase tracking-wider text-[#3D4035]/45">
                {label}
              </span>
            </div>
          ))}
        </div>
        {partial && (
          <p className="mt-2 text-[13px] text-[#3D4035]/40">
            {(T.nutritionPartial[lang] || T.nutritionPartial.en)(
              meal.nutrition_resolved,
              meal.nutrition_total
            )}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#3D4035]">
      <div className="mx-auto w-full max-w-[430px] px-4 pb-40 pt-8">
        {/* Header */}
        <p className="mb-1.5 text-center text-[12px] uppercase tracking-[0.14em] text-[#D2735A]">
          {L(T.eyebrow)}
        </p>
        <h1 className="text-center font-heading text-[32px] leading-tight">{L(T.title)}</h1>
        <p className="mx-auto mt-2 mb-6 max-w-[330px] text-center text-[15px] leading-relaxed text-[#3D4035]/55">
          {L(T.sub)}
        </p>

        {/* Segmented tabs — same shape as the app's pill controls */}
        <div className="mb-6 grid grid-cols-2 gap-1.5 rounded-full bg-black/[0.045] p-1.5">
          {[
            ["fridge", L(T.tabFridge)],
            ["plan", L(T.tabPlan)],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`rounded-full py-2 text-[15px] transition ${
                tab === k
                  ? "bg-[#7B9E7F] font-medium text-white shadow-sm"
                  : "text-[#3D4035]/50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ───────────── FRIDGE ───────────── */}
        {tab === "fridge" && (
          <div className="animate-[fadeIn_.25s_ease]">
            {/* Add row */}
            <div className="relative mb-5">
              <div className="flex gap-2">
                <input
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    setNotFound(null);
                  }}
                  onKeyDown={(e) => e.key === "Enter" && addDraft()}
                  maxLength={60}
                  disabled={items.length >= MAX_ITEMS}
                  placeholder={L(T.addPlaceholder)}
                  className="flex-1 rounded-full border border-black/5 bg-white px-4 py-3 text-[16px] outline-none transition focus:border-[#7B9E7F]/50 disabled:opacity-50"
                />
                <button
                  onClick={addDraft}
                  disabled={!draft.trim() || checking || items.length >= MAX_ITEMS}
                  aria-label="add"
                  className="h-[46px] w-[46px] shrink-0 rounded-full bg-[#7B9E7F] text-[24px] leading-none text-white transition active:scale-90 disabled:opacity-30"
                >
                  +
                </button>
              </div>

              {draft.trim() && suggestions.length > 0 && (
                <div className="absolute inset-x-0 top-[54px] z-20 overflow-hidden rounded-2xl border border-black/5 bg-white shadow-[0_8px_24px_rgba(0,0,0,0.10)]">
                  {suggestions.map((s, i) => (
                    <button
                      key={`${s.name}-${i}`}
                      onClick={() => pushItem(s.name, s.icon)}
                      className="flex w-full items-center gap-3 border-b border-black/[0.04] px-4 py-2.5 text-left last:border-0 active:bg-black/[0.03]"
                    >
                      <span className="text-[20px]">{s.icon || emojiFor(s.name)}</span>
                      <span className="flex-1 truncate text-[16px]">{s.name}</span>
                      {s.parent ? (
                        <span className="max-w-[38%] truncate text-[13px] text-[#3D4035]/35">
                          {s.parent}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {notFound && (
              <div className="mb-5 rounded-2xl bg-[#E9C46A]/12 px-4 py-3 text-[15px] leading-relaxed text-[#3D4035]/70">
                {T.notFound[lang] ? T.notFound[lang](notFound) : T.notFound.en(notFound)}
              </div>
            )}

            {/* Your items */}
            <div className="mb-1.5 flex items-baseline justify-between">
              <span className="text-[12px] uppercase tracking-[0.1em] text-[#3D4035]/40">
                {L(T.yourItems)}
              </span>
              <span className="text-[12px] text-[#3D4035]/35">
                {items.length} / {MAX_ITEMS}
              </span>
            </div>

            {items.length === 0 ? (
              <div className="mb-6 rounded-2xl border border-dashed border-black/10 px-4 py-8 text-center text-[15px] text-[#3D4035]/40">
                {L(T.emptyHint)}
              </div>
            ) : (
              <div className="mb-6 grid grid-cols-3 gap-2">
                {items.map((it, i) => (
                  <Tile
                    key={`${it.name}-${i}`}
                    label={it.name}
                    emoji={it.icon || emojiFor(it.name)}
                    action="✕"
                    onClick={() => setItems((p) => p.filter((_, x) => x !== i))}
                  />
                ))}
              </div>
            )}

            {/* Basics */}
            <div className="mb-1.5 flex items-baseline justify-between">
              <span className="text-[12px] uppercase tracking-[0.1em] text-[#3D4035]/40">
                {L(T.basics)}
              </span>
            </div>
            <p className="mb-3 text-[13px] leading-relaxed text-[#3D4035]/45">{L(T.basicsNote)}</p>
            <div className="grid grid-cols-4 gap-2">
              {staples.map((s, i) => {
                const gone = removed.includes(i);
                return (
                  <Tile
                    key={s}
                    label={s}
                    emoji={emojiFor(s)}
                    muted={gone}
                    action={gone ? "＋" : "✕"}
                    onClick={() =>
                      setRemoved((p) => (gone ? p.filter((x) => x !== i) : [...p, i]))
                    }
                  />
                );
              })}
            </div>

            {error && <p className="mt-4 text-center text-[15px] text-[#D2735A]">{error}</p>}
          </div>
        )}

        {/* ───────────── PLAN ───────────── */}
        {tab === "plan" && (
          <div className="animate-[fadeIn_.25s_ease]">
            {loading && (
              <div>
                <p className="mb-1 text-center text-[16px]">{L(T.generating)}</p>
                <p className="mb-6 text-center text-[13px] text-[#3D4035]/45">
                  {L(T.generatingNote)}
                </p>
                {["breakfast", "lunch", "dinner"].map((k) => (
                  <div key={k} className="mb-3">
                    <div className="mb-1.5 h-2 w-16 rounded bg-black/5" />
                    <div
                      className="h-[76px] animate-pulse rounded-xl border-l-[3px] bg-white"
                      style={{ borderLeftColor: ACCENT[k] }}
                    />
                  </div>
                ))}
              </div>
            )}

            {!loading && capped && (
              <div className="rounded-2xl bg-white p-6 text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                <p className="mb-1 font-heading text-[22px]">{L(T.capTitle)}</p>
                <p className="mb-5 text-[15px] leading-relaxed text-[#3D4035]/60">{L(T.capBody)}</p>
                <a
                  href="/auth?mode=signup"
                  onClick={() => reportDemoEvent("demo_cta")}
                  className="block rounded-full bg-[#7B9E7F] py-3.5 text-[16px] font-medium text-white"
                >
                  {L(T.cta)}
                </a>
              </div>
            )}

            {!loading && !capped && error && (
              <p className="rounded-2xl bg-white p-6 text-center text-[15px] text-[#D2735A]">
                {error}
              </p>
            )}

            {!loading && !capped && !error && !meals.length && (
              <div className="rounded-2xl border border-dashed border-black/10 px-4 py-10 text-center text-[15px] text-[#3D4035]/40">
                {L(T.emptyHint)}
              </div>
            )}

            {/* Recipe detail */}
            {!loading && openMeal !== null && meals[openMeal] && (
              <article className="animate-[fadeIn_.2s_ease]">
                <button
                  onClick={() => setOpenMeal(null)}
                  className="mb-4 text-[15px] text-[#7B9E7F]"
                >
                  ← {L(T.back)}
                </button>
                <h2 className="mb-1.5 font-heading text-[26px] leading-snug">
                  {meals[openMeal].recipe_title}
                </h2>
                <p className="mb-4 text-[15px] leading-relaxed text-[#3D4035]/55">
                  {meals[openMeal].recipe_description}
                </p>
                <div className="mb-6 flex flex-wrap gap-2">
                  {meals[openMeal].cook_time ? (
                    <span className="rounded-full bg-white px-3 py-1.5 text-[13px] shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                      ⏱ {meals[openMeal].cook_time} {L(T.min)}
                    </span>
                  ) : null}
                  {formatPriceRange(
                    meals[openMeal].cost?.cost_min_total,
                    meals[openMeal].cost?.cost_max_total
                  ) ? (
                    <span className="rounded-full bg-[#7B9E7F]/10 px-3 py-1.5 text-[13px] font-medium text-[#5C7A5E]">
                      {formatPriceRange(
                        meals[openMeal].cost.cost_min_total,
                        meals[openMeal].cost.cost_max_total
                      )}
                    </span>
                  ) : null}
                </div>

                <NutritionStrip meal={meals[openMeal]} />

                <p className="mb-2 text-[12px] uppercase tracking-[0.1em] text-[#3D4035]/40">
                  {L(T.ingredients)}
                </p>
                <div className="mb-6 overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                  {(meals[openMeal].ingredients || []).map((ing, i) => {
                    const priced = (meals[openMeal].cost?.items || []).find(
                      (x) => x.name === ing.name
                    );
                    return (
                      <div
                        key={i}
                        className="flex items-center gap-3 border-b border-black/[0.04] px-4 py-2.5 last:border-0"
                      >
                        <span className="text-[18px]">{emojiFor(ing.name)}</span>
                        <span className="flex-1 text-[15px]">{ing.name}</span>
                        <span className="text-[13px] text-[#3D4035]/40">
                          {ing.amount} {ing.unit || ""}
                        </span>
                        {priced?.cost ? (
                          <span className="w-[54px] text-right text-[13px] text-[#3D4035]/55">
                            {money(priced.cost)}
                          </span>
                        ) : (
                          <span className="w-[54px]" />
                        )}
                      </div>
                    );
                  })}
                </div>

                <p className="mb-2 text-[12px] uppercase tracking-[0.1em] text-[#3D4035]/40">
                  {L(T.method)}
                </p>
                <ol className="space-y-3">
                  {(meals[openMeal].instructions || []).map((s, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#7B9E7F]/12 text-[12px] font-medium text-[#5C7A5E]">
                        {i + 1}
                      </span>
                      <span className="text-[15px] leading-relaxed text-[#3D4035]/75">{s}</span>
                    </li>
                  ))}
                </ol>
              </article>
            )}

            {/* Day overview — laid out like the planner */}
            {!loading && openMeal === null && meals.length > 0 && (
              <div className="animate-[fadeIn_.2s_ease]">
                {meals.map((m, i) => {
                  const accent = ACCENT[m.meal_type] || ACCENT.snack;
                  return (
                    <div key={i} className="mb-4">
                      <p className="mb-1.5 text-[12px] uppercase tracking-[0.1em] text-[#3D4035]/40">
                        {m.meal_type}
                      </p>
                      <button
                        onClick={() => setOpenMeal(i)}
                        style={{ borderLeftColor: accent }}
                        className="w-full rounded-xl border-l-[3px] bg-white p-4 text-left shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition active:scale-[0.99]"
                      >
                        <div className="mb-1 flex items-start gap-3">
                          <span className="flex-1 text-[16px] font-medium leading-snug">
                            {m.recipe_title}
                          </span>
                          <span className="shrink-0 text-right text-[11px] leading-relaxed text-[#3D4035]/40">
                            {m.calories ? <>🔥 {m.calories}<br /></> : null}
                            {m.cook_time ? <>⏱ {m.cook_time} {L(T.min)}</> : null}
                          </span>
                        </div>
                        <p className="mb-3 line-clamp-2 text-[13px] leading-relaxed text-[#3D4035]/50">
                          {m.recipe_description}
                        </p>
                        <div className="flex items-center">
                          <span className="text-[13px] text-[#7B9E7F]">→</span>
                          {formatPriceRange(
                            m.cost?.cost_min_total,
                            m.cost?.cost_max_total
                          ) ? (
                            <span className="ml-auto text-[13px] text-[#3D4035]/55">
                              {formatPriceRange(m.cost.cost_min_total, m.cost.cost_max_total)}
                            </span>
                          ) : null}
                        </div>
                      </button>
                    </div>
                  );
                })}

                {dayCost.max > 0 && (
                  <div className="mb-8 flex items-center justify-between rounded-xl bg-black/[0.03] px-4 py-3">
                    <span className="text-[13px] text-[#3D4035]/55">{L(T.dayTotal)}</span>
                    <span className="text-[16px] font-medium">
                      {formatPriceRange(dayCost.min, dayCost.max)}
                    </span>
                  </div>
                )}

                <div className="rounded-2xl bg-white p-6 text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                  <p className="mb-1 font-heading text-[22px]">{L(T.outroTitle)}</p>
                  <p className="mb-5 text-[15px] leading-relaxed text-[#3D4035]/60">
                    {L(T.outroBody)}
                  </p>
                  <a
                    href="/auth?mode=signup"
                    onClick={() => reportDemoEvent("demo_cta")}
                    className="block rounded-full bg-[#7B9E7F] py-3.5 text-[16px] font-medium text-white"
                  >
                    {L(T.cta)}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sticky action, where the app keeps its bottom bar */}
      {tab === "fridge" && (
        <div className="fixed inset-x-0 bottom-0 bg-gradient-to-t from-[#FAFAF8] via-[#FAFAF8] to-transparent pb-5 pt-8">
          <div className="mx-auto w-full max-w-[430px] px-4">
            <button
              onClick={generate}
              disabled={!ready || loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#7B9E7F] py-4 text-[16px] font-medium text-white shadow-lg shadow-[#7B9E7F]/20 transition active:scale-[0.98] disabled:bg-[#7B9E7F]/25 disabled:shadow-none"
            >
              ✨ {loading ? L(T.generating) : L(T.generate)}
            </button>
            <p className="mt-2 text-center text-[12px] text-[#3D4035]/40">
              {ready ? L(T.fromFridge) : T.needMore[lang] ? T.needMore[lang](needed) : T.needMore.en(needed)}
            </p>
          </div>
        </div>
      )}

      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}
